import { Injectable } from '@angular/core';
import { HttpClientService } from '../core/services/http-client.service';
import { map } from 'rxjs/operators';
import { of } from 'rxjs';
import { CollegeDetail } from '../models';
import { filenameDateStamp } from '../utils/filename-datestamp.util';

@Injectable({
  providedIn: 'root',
})
export class CollegeReconService {
  private lpsDataCache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor(private httpClient: HttpClientService) { }

  getCollegeProfile(userId: string) {
    return this.httpClient.get(`/setting/collegeuser/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getOnlineBahRate() {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/admin/setting/online_bah_rate/?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getStudentBahData(studentId: any) {
    return this.httpClient.get(`/student/save/bahdata/${studentId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getMycaaData(collegeId: number) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/college/mycaa/data/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getMajorsList(text: string) {
    return this.httpClient.get(`/majors/${text}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getDegreeLevels() {
    return this.httpClient.get(`/levels`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getCollegeLevelWithDesignation(collegeId: string) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/collegemajors/designation/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getMajorDataWithBucket(collegeId: string) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/majors/buckets-title/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getBucketList() {
    return this.httpClient.get(`/bucketlist`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getSBucketList() {
    return this.httpClient.get(`/secondarybucketlist/`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getSecondaryBucketData(bucketIds: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/secondarybucketlist/${bucketIds}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getPBucketList(pdata: any) {
    return this.httpClient.post(`/secondarylist/level/pbucket`, pdata).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getRankList() {
    return this.httpClient.get(`/collegerank/ranklist`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getAllBranch() {
    const newdate = filenameDateStamp();
    return this.httpClient.get(`/branch?dt=${newdate}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getRotcBranch(collegeId: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/college/rotc-branch-url/${collegeId}?d=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getBranchList() {
    return this.httpClient.get(`/branch`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getMilitaryStatusList() {
    return this.httpClient.get(`/military-status`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getAwards() {
    const newdate = filenameDateStamp();
    return this.httpClient.get(`/award?dt=${newdate}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getZipCodeNumber(searchText: any) {
    const newdate = filenameDateStamp();
    const searchUrl = searchText
      ? `/bah/search/zipcode/${searchText}`
      : '/bah/search/zipcode';
    return this.httpClient.get(`${searchUrl}?dt=${newdate}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getZipCodeV2(searchText: any) {
    return this.httpClient.get(`/calculator/bah/zipcode?searchText=${searchText}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getCountryList() {
    return this.httpClient.get(`/calculator/oha/distinct`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getOHARankList() {
    return this.httpClient.get(`/calculator/oha/ranks`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getLocalityList(country: string) {
    return this.httpClient.get(`/calculator/oha/locality/${country}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getOHASettings(country: string) {
    return this.httpClient.get(`/calculator/oha/settings/${country}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  calculateOha(data: any) {
    return this.httpClient.post(`/calculator/oha/calculate`, data).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getForeignExchangeRate(currency: string) {
    return this.httpClient.get(`/calculator/daily-forex/${currency}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getCreditEstimatorInfo(data: any) {
    return this.httpClient.post(`/credit/estimator`, data).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getBahCalculatorCity(stateId: number) {
    return this.httpClient.get(`/bah/calculator/city/` + stateId).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getBahCalculatorData() {
    const newdate = filenameDateStamp();
    return this.httpClient.get(`/bah/calculator/datalist?d=${newdate}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getautoCollegeListByName(name: string) {
    return this.httpClient.get(`/autocollege/listby/name`, { name }).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getOhaCalculatorCountryData() {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/oha/calculator/country/datalist?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getOhaCalculatorLocalityData(countryId: string) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get(`/oha/calculator/locality/${countryId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  calculateBah(data: any) {
    return this.httpClient.post(`/bah/calculate`, data).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  calculateBahV2(data: any) {
    return this.httpClient.post(`/calculator/bah/calculate`, data).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getOhaCalculatorBahRate(data: any) {
    return this.httpClient
      .post(`/oha/calculator/bahdata`, data)
      .pipe(map((response: any) => response.data));
  }

  getGiBillCalculatorData() {
    const newdate = filenameDateStamp();
    return this.httpClient.get(`/gibill/datalist?d=${newdate}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateMessageReadState(readInfo: any) {
    return this.httpClient.post(`/reconmessage/markread`, readInfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateRotcBranch(collegeId: any, rotcBranchBody: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .post(
        `/college/rotc-branch-url/${collegeId}?d=${newdate}`,
        rotcBranchBody,
      )
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  updateCollege(collegeModel: any) {
    return this.httpClient
      .post(`/college/update/collegemilitarydata`, collegeModel)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  updateCollegePasswordSetting(settinginfo: any) {
    return this.httpClient.post(`/setting/resetpassword`, settinginfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateCollegeEmailSetting(settingInfo: any) {
    return this.httpClient.post(`/setting/email`, settingInfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateCollegeInfo(model: any) {
    return this.httpClient.post(`/college/update/collegelocation`, model).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  fetchVeteranScholarshipInfo(userId: string) {
    return this.httpClient.get(`/scholarship/userinfo/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  viewCounter(viewInfo: any) {
    return this.httpClient.post(`/usagestatistics/counter/view`, viewInfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  counterSearchCollege(cdata: any) {
    return this.httpClient
      .post('/usagestatistics/counter/searchcollege', cdata)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  collegeReplyMessage(messageinfo: any) {
    return this.httpClient.post(`/college/replymessage`, messageinfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getCollegeRankData() {
    const newdate = filenameDateStamp();
    return this.httpClient.get('/collegerank/ranklist' + '?dt=' + newdate).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  trackVeteranRegistration(pdata: any) {
    return this.httpClient.post('/veterans/registrations', pdata).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  collegeFilterTracking(trackinfo: any) {
    return this.httpClient.post('/college/filter/tracking', trackinfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  //for email bounceback
  getNewRegisterCollegeData(searchDatas: any) {
    return this.httpClient
      .post('/emailbounceback/match/collegelist', searchDatas)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  //match paid college lists
  getPaidCollegeData(searchDatas: any) {
    return this.httpClient.post('/paid/match/collegelist', searchDatas).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  // /emailbounceback/autosendemail/newuser
  //for sending the bounceback email
  // sendBounceBackEmailToUser(data: any) {
  //   return this.httpClient
  //     .post('/emailbounceback/autosendemail/newuser', data)
  //     .pipe(
  //       map((response: any) => {
  //         return response.data;
  //       }),
  //     );
  // }

  //bucket specific bounceback
  // getNewRegisterBucketData(completeData: any) {
  //   return this.httpClient
  //     .post('/emailbounceback/match/bucketdata', completeData)
  //     .pipe(
  //       map((response: any) => {
  //         return response.data;
  //       }),
  //     );
  // }

  saveBahInfoData(bahData: any) {
    return this.httpClient.post('/veteran/save/bahinfo', bahData).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  // updateBahInfoData(studentId: string, bahData: any) {
  //   return this.httpClient
  //     .put(`/veteran/update-bahinfo/${studentId}`, bahData)
  //     .pipe(
  //       map((response: any) => {
  //         return response.data;
  //       }),
  //     );
  // }

  // updateOHAInfoData(studentId: string, ohaData: any) {
  //   return this.httpClient
  //     .put(`/veteran/update-ohainfo/${studentId}`, ohaData)
  //     .pipe(
  //       map((response: any) => {
  //         return response.data;
  //       }),
  //     );
  // }

  // updateMHAInfoData(studentId: string, mhaData: any) {
  //   return this.httpClient
  //     .put(`/veteran/update-mhainfo/${studentId}`, mhaData)
  //     .pipe(
  //       map((response: any) => {
  //         return response.data;
  //       }),
  //     );
  // }

  saveBasInfoData(basData: any) {
    return this.httpClient.post('/veteran/save/basinfo', basData).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  saveVaDisabilityInfoData(data: any) {
    return this.httpClient.post('/veteran/save/va-disability-info', data).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  saveBahCalulcatedData(bahData: any) {
    return this.httpClient.post('/save/veteran/bahdata', bahData).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  saveStudentInsuraceData(insuranceData: any) {
    return this.httpClient
      .post('/save/veteran/insurancedata', insuranceData)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getStudentInsuraceData(studentId: any) {
    return this.httpClient.get(`/student/save/insurancedata/${studentId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getCollegeIdAlias(calias: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get('/collegealias/collegeid/' + calias + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getCollegeDetail(collegeid: any) {
    const newdate = filenameDateStamp();
    return this.httpClient.get('/college/' + collegeid + '?dt=' + newdate).pipe(
      map((response: any) => {
        let results: CollegeDetail;
        results = response.data;
        return results;
      }),
    );
  }

  getMatchedParentChildCollege(postData: any) {
    return this.httpClient
      .post('/veteran/multicampus/matched/collegelist', postData)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getRegisterCollegeEmailData(emailDatas: any) {
    return this.httpClient.post('/register/match/emaildata', emailDatas).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getStudentMessageInfo(studentId: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get('/student/messageinfo/' + studentId + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getVeteranSecondaryBucket(studentId: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get('/student/secondary/bucket/' + studentId + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getRegisterCollegeData(searchDatas: any) {
    return this.httpClient
      .post('/register/match/collegelist', searchDatas)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  searchResultCollegesTracking(trackinfo: any) {
    return this.httpClient
      .post('/searchresult/colleges/tracking', trackinfo)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  checkVeteranExist(sid: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get('/veterans/exist/' + sid + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  updateNagEmailSubscription(stuinfo: any) {
    return this.httpClient.post('/nagemail/subscription', stuinfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  collegeSearchTracking(trackinfo: any) {
    return this.httpClient.post('/college/search/tracking', trackinfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getPrimaryBahDataInfo(type: any, sid: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get('/student/getprimaryinfo/' + type + '/' + sid + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getGradSchoolProgramTuition(cid: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get('/college/grad-programs/' + cid + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getBahDataList(type: any, sid: any) {
    const newdate = filenameDateStamp();
    return this.httpClient
      .get('/student/getbahdata/' + type + '/' + sid + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  tabClickTracking(tabdata: any) {
    return this.httpClient.post('/college/tabs/tracking', tabdata).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  homepageLinkTracking(homepageData: any) {
    return this.httpClient
      .post('/college/homepagelink/tracking', homepageData)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getLookup(params: any) {
    return this.httpClient.get(`/superadmin/lookup`, params).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getSqfPreferredDefaults() {
    return this.httpClient.get('/lps/sqf-preferred-defaults').pipe(
      map((response: any) => response.data as { [bucketId: number]: number[] })
    );
  }

  getLPSData(alias: string) {
    // Check cache first
    const cached = this.lpsDataCache.get(alias);
    const now = Date.now();

    if (cached && now - cached.timestamp < this.CACHE_DURATION) {
      return of(cached.data);
    }

    // Fetch fresh data and cache it
    return this.httpClient.get(`/lps/source/${alias}`).pipe(
      map((response: any) => {
        const data = response.data;
        this.lpsDataCache.set(alias, { data, timestamp: now });
        return data;
      }),
    );
  }
}
